import { inject, Injectable } from '@angular/core';
import { delay, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { onZefDialogClose, onZefDialogOpen, zefDialogClose, zefDialogOpen } from '@zerops/zef/dialog';
import { IntegrationTokenEntity } from '@zerops/zerops/core/integration-token-base';
import { integrationTokenDelegationActions } from '@zerops/zerops/core/integration-token-delegation-base';
import { UserTokenEntity } from '@zerops/zerops/core/user-token-base';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { successOf } from '@zerops/zef/entities';
import { box } from 'ngrx-forms';
import { IntegrationTokenRoleCode } from '@zerops/models/integration-token';
import { ClientUserRole } from '@zerops/models/client-user';
import { FEATURE_NAME } from './token-add-edit-dialog.constant';
import { TokenAddEditForm, TokenAddEditFormValue } from './token-add-edit.form';
import { DelegationCreateForm } from './token-delegation.form';
import { TokenAddEditMode } from './token-add-edit.model';

@Injectable()
export class TokenAddEditDialogEffect {
  #actions$ = inject(Actions);
  #userTokenEntity = inject(UserTokenEntity);
  #integrationTokenEntity = inject(IntegrationTokenEntity);
  #userEntity = inject(UserEntity);
  #form = inject(TokenAddEditForm);
  #delegationForm = inject(DelegationCreateForm);

  // # Success Streams
  #onUserTokenAddSuccess$ = this.#actions$.pipe(
    successOf(this.#userTokenEntity.addOne)
  );

  #onIntegrationTokenAddSuccess$ = this.#actions$.pipe(
    successOf(this.#integrationTokenEntity.addOne)
  );

  #onIntegrationTokenUpdateSuccess$ = this.#actions$.pipe(
    successOf(this.#integrationTokenEntity.updateOne)
  );

  // ============================================================================
  // CRITICAL EFFECT: Setup Form on Dialog Open
  // ============================================================================
  // This MUST happen in effects, NOT in component dispatcher!
  // Sets the form 'mode' field and populates data for edit mode
  // Combined into single effect to prevent race conditions
  onDialogOpenSetup$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(zefDialogOpen),
      filter((action) => action?.key === FEATURE_NAME && !!action?.meta?.mode),
      withLatestFrom(this.#userEntity.activeClientUser$),
      switchMap(([ { meta }, currentUser ]) => {
        const mode = meta.mode as TokenAddEditMode;
        const formMode = mode === TokenAddEditMode.ADD_USER ? 'user' : 'integration';

        // For EDIT mode: fetch entity and populate, otherwise just set mode
        if (mode === TokenAddEditMode.EDIT_INTEGRATION && meta.tokenId) {
          return this.#integrationTokenEntity.entityById$(meta.tokenId as string).pipe(
            filter((token) => !!token),
            take(1),
            map((token) => {
              const formValue: Partial<TokenAddEditFormValue> = {
                name: token.name,
                mode: 'integration',
                roleCode: token.roleCode,
                canCreateProjects: token.canCreateProjects || false,
                projects: box(token.projects || [])
              };

              return this.#form.setValue(formValue);
            })
          );
        }

        // For ADD_INTEGRATION mode: determine roleCode based on user's role
        if (mode === TokenAddEditMode.ADD_INTEGRATION && currentUser) {
          // NO_ACCESS users can only create NO_ACCESS tokens - auto-select it
          if (currentUser.roleCode === ClientUserRole.NoAccess) {
            return [
              this.#form.setValue('mode', formMode),
              this.#form.setValue('roleCode', IntegrationTokenRoleCode.NoAccess)
            ];
          }
        }

        // For ADD_USER or ADD_INTEGRATION with full access: just set the mode
        return [ this.#form.setValue('mode', formMode) ];
      })
    )
  );

  // Load just this token's delegations when opening the edit dialog (scoped —
  // avoids pulling every delegation on the client).
  onDialogOpenLoadDelegations$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(zefDialogOpen),
      filter((action) =>
        action?.key === FEATURE_NAME
        && action?.meta?.mode === TokenAddEditMode.EDIT_INTEGRATION
        && !!action?.meta?.tokenId
      ),
      map((action) => integrationTokenDelegationActions.loadTokenDelegations({
        tokenId: action.meta.tokenId as string
      }))
    )
  );

  // ============================================================================
  // SUCCESS HANDLING: Close Dialog After Token Creation
  // ============================================================================
  // Close add/edit dialog after successful user token creation
  onUserTokenAddSuccessClose$ = createEffect(() =>
    this.#onUserTokenAddSuccess$.pipe(
      map(() => zefDialogClose({ key: FEATURE_NAME }))
    )
  );

  // Close add/edit dialog after successful integration token creation
  onIntegrationTokenAddSuccessClose$ = createEffect(() =>
    this.#onIntegrationTokenAddSuccess$.pipe(
      map(() => zefDialogClose({ key: FEATURE_NAME }))
    )
  );

  // Close add/edit dialog after successful integration token update (edit mode)
  onIntegrationTokenUpdateSuccessClose$ = createEffect(() =>
    this.#onIntegrationTokenUpdateSuccess$.pipe(
      map(() => zefDialogClose({ key: FEATURE_NAME }))
    )
  );

  // ============================================================================
  // CLEANUP: Reset Everything on Dialog Close
  // ============================================================================
  // Reset dialog state on close
  onDialogCloseResetDialog$ = createEffect(() =>
    this.#actions$.pipe(
      onZefDialogClose(FEATURE_NAME),
      mergeMap(() => [
        this.#form.reset(),
        this.#form.setDefaultValues(),
        this.#delegationForm.reset(),
        this.#delegationForm.setDefaultValues()
      ])
    )
  );

  // ============================================================================
  // FOCUS: Focus Name Input on Dialog Open
  // ============================================================================
  onDialogOpenFocusInput$ = createEffect(() =>
    this.#actions$.pipe(
      onZefDialogOpen(FEATURE_NAME),
      delay(100),
      map(() => this.#form.focus('name'))
    )
  );
}
